// Frame.io migration panel — Storage Phase C.
//
// Admin-only, and deliberately collapsed by default: this is a decommissioning
// tool that will be used a handful of times, not a daily control. It should not
// compete for attention with the upload buttons above it.
//
// The flow is strictly: connect → paste a URL → PRE-FLIGHT → confirm → transfer.
// Nothing moves until the operator has seen the counts and pressed the button,
// and a merge into an existing folder is always called out explicitly rather
// than happening quietly.

import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../lib/api';
import { formatBytes } from '../lib/folderUpload';

const BASE = '/api/storage-archive/frameio';

const ACCENT = 'var(--s-nr-t)';

function errMsg(err, fallback) {
  return err?.response?.data?.error || err?.message || fallback;
}

const box = {
  background: 'var(--bg-card)', border: '1px solid var(--border-mid)',
  borderRadius: 10, padding: 16, marginBottom: 16,
};
const btn = (enabled, primary) => ({
  background: !enabled ? 'var(--bg-input)' : primary ? ACCENT : 'transparent',
  color: !enabled ? 'var(--tx-muted)' : primary ? '#fff' : 'var(--tx-primary)',
  border: `1px solid ${enabled ? ACCENT : 'var(--border-mid)'}`,
  borderRadius: 8, padding: '8px 14px', fontSize: 13, fontWeight: 600,
  fontFamily: 'var(--font)', cursor: enabled ? 'pointer' : 'not-allowed',
});
const input = {
  background: 'var(--bg-input)', border: '1px solid var(--border-mid)',
  borderRadius: 8, padding: '8px 12px', color: 'var(--tx-primary)',
  fontSize: 13, fontFamily: 'var(--font)',
};
const label = { fontSize: 11, color: 'var(--tx-muted)', textTransform: 'uppercase', letterSpacing: 0.4 };

// ─── Confirmation dialog (spec §6.3) ──────────────────────────────────────────

function ConfirmDialog({ preflight, rootName, setRootName, onConfirm, onCancel, busy }) {
  const p = preflight;
  const blocked = p.blocked;

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,.55)', zIndex: 400,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
    }}>
      <div style={{
        background: 'var(--bg-card)', border: '1px solid var(--border-mid)', borderRadius: 12,
        width: 'min(680px, 100%)', maxHeight: '85vh', overflowY: 'auto', padding: 22,
      }}>
        <h3 style={{ margin: '0 0 4px', fontSize: 16, color: 'var(--tx-primary)' }}>
          Migrate from Frame.io
        </h3>
        <p style={{ margin: '0 0 16px', fontSize: 12, color: 'var(--tx-muted)' }}>
          Nothing has been copied yet. Review this before starting.
        </p>

        {/* The name came from the API, not from the URL. Editable — but the
            rename applies to the root folder only. */}
        <div style={{ marginBottom: 14 }}>
          <div style={label}>Destination folder name</div>
          <input
            value={rootName} onChange={e => setRootName(e.target.value)}
            style={{ ...input, width: '100%', marginTop: 5 }}
          />
          <div style={{ fontSize: 11, color: 'var(--tx-muted)', marginTop: 5 }}>
            From Frame.io: <strong>{p.derivedName}</strong>. Renaming applies to this
            top folder only — everything inside keeps its original Frame.io name.
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(120px,1fr))', gap: 10, marginBottom: 14 }}>
          {[
            ['Files', p.fileCount.toLocaleString()],
            ['Folders', p.folderCount.toLocaleString()],
            ['New folders', p.foldersNew.toLocaleString()],
            ['Total size', formatBytes(p.totalBytes)],
          ].map(([k, v]) => (
            <div key={k} style={{ background: 'var(--bg-input)', borderRadius: 8, padding: '9px 11px' }}>
              <div style={label}>{k}</div>
              <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--tx-primary)', marginTop: 2 }}>{v}</div>
            </div>
          ))}
        </div>

        {/* A merge is NEVER silent. */}
        {p.mergesIntoExisting && (
          <div style={{
            background: 'var(--s-revision)', color: 'var(--s-revision-t)',
            border: '1px solid var(--s-revision-t)', borderRadius: 8, padding: '10px 12px',
            fontSize: 12, marginBottom: 12,
          }}>
            <strong>This merges into {p.mergeTargetCount} folder{p.mergeTargetCount === 1 ? '' : 's'} that already exist.</strong>
            {p.rootIsMerge && <> The destination folder <strong>{rootName}</strong> already exists — its contents will be added to, not replaced.</>}
            <div style={{ marginTop: 6, maxHeight: 90, overflowY: 'auto', fontFamily: 'monospace', fontSize: 11 }}>
              {p.mergeTargets.map(t => <div key={t}>{t}</div>)}
              {p.mergeTargetCount > p.mergeTargets.length && <div>…and {p.mergeTargetCount - p.mergeTargets.length} more</div>}
            </div>
          </div>
        )}

        {p.capBreach && (
          <div style={{ background: 'var(--s-incomplete)', color: 'var(--s-incomplete-t)', border: '1px solid var(--s-incomplete-t)', borderRadius: 8, padding: '10px 12px', fontSize: 12, marginBottom: 12 }}>
            <strong>Folder limit exceeded.</strong> {p.capBreach.message}
          </div>
        )}
        {p.dryRunError && (
          <div style={{ background: 'var(--s-incomplete)', color: 'var(--s-incomplete-t)', border: '1px solid var(--s-incomplete-t)', borderRadius: 8, padding: '10px 12px', fontSize: 12, marginBottom: 12 }}>
            <strong>Cannot create the folder tree.</strong> {p.dryRunError.message}
          </div>
        )}
        {p.truncated && (
          <div style={{ background: 'var(--s-incomplete)', color: 'var(--s-incomplete-t)', border: '1px solid var(--s-incomplete-t)', borderRadius: 8, padding: '10px 12px', fontSize: 12, marginBottom: 12 }}>
            <strong>That tree is too large to walk in one pass.</strong> Migrate one project at a time.
          </div>
        )}

        {p.skippedCount > 0 && (
          <details style={{ marginBottom: 12 }}>
            <summary style={{ fontSize: 12, color: 'var(--tx-muted)', cursor: 'pointer' }}>
              {p.skippedCount} item{p.skippedCount === 1 ? '' : 's'} will be skipped
            </summary>
            <div style={{ maxHeight: 120, overflowY: 'auto', fontSize: 11, marginTop: 6, color: 'var(--tx-muted)' }}>
              {p.skipped.map((s, i) => (
                <div key={i}>{s.name || s.id} — {s.reason}</div>
              ))}
            </div>
          </details>
        )}

        {p.sanitizedNames?.length > 0 && (
          <details style={{ marginBottom: 12 }}>
            <summary style={{ fontSize: 12, color: 'var(--tx-muted)', cursor: 'pointer' }}>
              {p.sanitizedNames.length} folder name{p.sanitizedNames.length === 1 ? '' : 's'} will be cleaned up
            </summary>
            <div style={{ maxHeight: 120, overflowY: 'auto', fontSize: 11, marginTop: 6, fontFamily: 'monospace', color: 'var(--tx-muted)' }}>
              {p.sanitizedNames.map((s, i) => <div key={i}>{s.from} → {s.to}</div>)}
            </div>
          </details>
        )}

        {/* Observed, not assumed — this is the live answer to whether Frame.io
            hands back version stacks at all. */}
        {p.versionStackCount > 0 && (
          <p style={{ fontSize: 11, color: 'var(--tx-muted)', marginBottom: 12 }}>
            {p.versionStackCount} version stack{p.versionStackCount === 1 ? '' : 's'} found — the current
            version of each will be migrated, earlier versions will not.
          </p>
        )}

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 6 }}>
          <button onClick={onCancel} disabled={busy} style={btn(!busy, false)}>Cancel</button>
          <button
            onClick={onConfirm}
            disabled={blocked || busy || !rootName.trim()}
            style={btn(!blocked && !busy && !!rootName.trim(), true)}
          >
            {busy ? 'Starting…' : `Migrate ${p.fileCount.toLocaleString()} files`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Progress row ─────────────────────────────────────────────────────────────

function MigrationRow({ m, onResume }) {
  const pct = m.filesTotal ? Math.round((m.filesDone / m.filesTotal) * 100) : 0;
  const colour = m.status === 'complete' ? 'var(--s-approved-t)'
    : m.status === 'failed' ? 'var(--s-incomplete-t)'
    : m.status === 'running' ? ACCENT : 'var(--tx-muted)';

  return (
    <div style={{ borderTop: '1px solid var(--border)', padding: '10px 0' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <strong style={{ fontSize: 13, color: 'var(--tx-primary)' }}>{m.rootName}</strong>
        <span style={{ fontSize: 11, color: colour, fontWeight: 600, textTransform: 'uppercase' }}>{m.status}</span>
        <span style={{ fontSize: 11, color: 'var(--tx-muted)' }}>
          {m.filesDone}/{m.filesTotal} files
          {m.filesFailed > 0 && <span style={{ color: 'var(--s-incomplete-t)' }}> · {m.filesFailed} failed</span>}
          {' · '}{formatBytes(Number(m.bytesDone))} of {formatBytes(Number(m.bytesTotal))}
        </span>
        {(m.status === 'failed' || (m.status !== 'running' && m.filesFailed > 0)) && (
          <button onClick={() => onResume(m.id)} style={{ ...btn(true, false), padding: '4px 10px', fontSize: 11 }}>
            Resume
          </button>
        )}
      </div>
      <div style={{ height: 4, background: 'var(--bg-input)', borderRadius: 3, marginTop: 6, overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: colour, transition: 'width .3s' }} />
      </div>
      {m.error && <div style={{ fontSize: 11, color: 'var(--s-incomplete-t)', marginTop: 5 }}>{m.error}</div>}
    </div>
  );
}

// ─── Panel ────────────────────────────────────────────────────────────────────

export default function FrameioMigrationPanel({ clientId, selectedFolderId, onMigrated }) {
  const [open, setOpen]       = useState(false);
  const [status, setStatus]   = useState(null);
  const [url, setUrl]         = useState('');
  const [busy, setBusy]       = useState(false);
  const [error, setError]     = useState(null);
  const [preflight, setPreflight] = useState(null);
  const [rootName, setRootName]   = useState('');
  const [migrations, setMigrations] = useState([]);
  const pollRef = useRef(null);

  const loadStatus = useCallback(() => {
    api.get(`${BASE}/status`).then(({ data }) => setStatus(data)).catch(() => setStatus(null));
  }, []);

  const loadMigrations = useCallback(() => {
    if (!clientId) { setMigrations([]); return; }
    api.get(`${BASE}/migrations`, { params: { clientId } })
      .then(({ data }) => setMigrations(data.rows || []))
      .catch(() => {});
  }, [clientId]);

  useEffect(() => { if (open) { loadStatus(); loadMigrations(); } }, [open, loadStatus, loadMigrations]);

  // Poll only while something is actually moving — a 15 TB job runs for days and
  // there is no reason to hammer the API when nothing is in flight.
  useEffect(() => {
    const active = migrations.some(m => m.status === 'running' || m.status === 'confirmed');
    clearInterval(pollRef.current);
    if (open && active) pollRef.current = setInterval(loadMigrations, 5000);
    return () => clearInterval(pollRef.current);
  }, [open, migrations, loadMigrations]);

  const connect = async () => {
    setError(null);
    try {
      const { data } = await api.post(`${BASE}/connect`);
      // Adobe's consent screen cannot render in an iframe or be followed by
      // fetch — it has to be a real top-level navigation in a new tab.
      window.open(data.authorizeUrl, '_blank', 'noopener');
    } catch (err) { setError(errMsg(err, 'Could not start the Frame.io connection.')); }
  };

  const disconnect = async () => {
    if (!window.confirm('Disconnect Frame.io? Any running migration will start failing.')) return;
    try { await api.post(`${BASE}/disconnect`); loadStatus(); }
    catch (err) { setError(errMsg(err, 'Could not disconnect.')); }
  };

  const runPreflight = async () => {
    setError(null); setBusy(true);
    try {
      const { data } = await api.post(`${BASE}/preflight`, {
        clientId,
        parentFolderId: selectedFolderId || null,
        url: url.trim(),
      });
      setPreflight(data);
      setRootName(data.rootName || data.derivedName || '');
    } catch (err) {
      setError(errMsg(err, 'Pre-flight failed.'));
    } finally { setBusy(false); }
  };

  const confirm = async () => {
    setBusy(true); setError(null);
    try {
      await api.post(`${BASE}/migrations`, {
        clientId,
        parentFolderId: selectedFolderId || null,
        // Hand back the opaque pre-flight id so the server reuses its OWN
        // stored tree walk instead of re-walking Frame.io (~7 min on a real
        // project). We never send the file list — only this id.
        preflightId: preflight.preflightId || null,
        accountId: preflight.accountId,
        folderId:  preflight.folderId,
        url:       preflight.sourceUrl,
        rootName:  rootName.trim(),
        expected:  { fileCount: preflight.fileCount },
      });
      setPreflight(null); setUrl('');
      loadMigrations();
      onMigrated?.();
    } catch (err) {
      setError(errMsg(err, 'Could not start the migration.'));
      setPreflight(null);
    } finally { setBusy(false); }
  };

  const resume = async (id) => {
    try { await api.post(`${BASE}/migrations/${id}/run`); loadMigrations(); }
    catch (err) { setError(errMsg(err, 'Could not resume.')); }
  };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        style={{ ...btn(true, false), marginBottom: 16, fontSize: 12, padding: '6px 12px' }}
      >
        ⇄ Frame.io migration
      </button>
    );
  }

  const connected  = status?.connected;
  const configured = status?.configured;
  const canPreflight = connected && !!clientId && !!url.trim() && !busy;

  return (
    <div style={box}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <div>
          <strong style={{ fontSize: 14, color: 'var(--tx-primary)' }}>Frame.io migration</strong>
          <div style={{ fontSize: 11, color: 'var(--tx-muted)', marginTop: 2 }}>
            Internal tool — copies a Frame.io folder tree into this client's archive.
          </div>
        </div>
        <button onClick={() => setOpen(false)} style={{ ...btn(true, false), padding: '4px 10px', fontSize: 11 }}>Close</button>
      </div>

      {error && (
        <div style={{ background: 'var(--s-incomplete)', color: 'var(--s-incomplete-t)', border: '1px solid var(--s-incomplete-t)', borderRadius: 8, padding: '9px 12px', fontSize: 12, marginBottom: 12 }}>
          {error}
        </div>
      )}

      {/* Connection */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
        <span style={{
          fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
          color: connected ? 'var(--s-approved-t)' : 'var(--tx-muted)',
        }}>
          {connected ? '● Connected' : '○ Not connected'}
        </span>
        {connected && status.adobeEmail && (
          <span style={{ fontSize: 11, color: 'var(--tx-muted)' }}>{status.adobeEmail}</span>
        )}
        {connected && status.rotationCount > 0 && (
          <span style={{ fontSize: 11, color: 'var(--tx-muted)' }}>
            token rotated {status.rotationCount}×
          </span>
        )}
        {!connected && <button onClick={connect} disabled={!configured} style={btn(!!configured, true)}>Connect Frame.io</button>}
        {connected && <button onClick={disconnect} style={btn(true, false)}>Disconnect</button>}
      </div>

      {!configured && status && (
        <div style={{ background: 'var(--s-incomplete)', color: 'var(--s-incomplete-t)', border: '1px solid var(--s-incomplete-t)', borderRadius: 8, padding: '9px 12px', fontSize: 12, marginBottom: 12 }}>
          Frame.io is not configured on this server — missing {(status.missing || []).join(', ')}.
          An administrator needs to add the Adobe credentials to the API environment.
        </div>
      )}

      {/* Source */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <input
          value={url} onChange={e => setUrl(e.target.value)}
          placeholder="Paste a Frame.io folder URL…"
          disabled={!connected}
          style={{ ...input, flex: 1, minWidth: 260 }}
        />
        <button onClick={runPreflight} disabled={!canPreflight} style={btn(canPreflight, true)}>
          {busy ? 'Checking…' : 'Check contents'}
        </button>
      </div>
      <div style={{ fontSize: 11, color: 'var(--tx-muted)', marginTop: 6 }}>
        {!clientId
          ? 'Select a project first.'
          : selectedFolderId
            ? 'Will be created inside the selected folder.'
            : 'Will be created at the project root.'}
      </div>

      {migrations.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <div style={label}>Migrations</div>
          {migrations.map(m => <MigrationRow key={m.id} m={m} onResume={resume} />)}
        </div>
      )}

      {preflight && (
        <ConfirmDialog
          preflight={preflight}
          rootName={rootName} setRootName={setRootName}
          onConfirm={confirm}
          onCancel={() => setPreflight(null)}
          busy={busy}
        />
      )}
    </div>
  );
}
